import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowLeft, ChevronDown, ChevronRight, History, Loader2, X } from "lucide-react";
import { toast } from "react-toastify";
import { type AuditLog, getAdminUserAuditLogs } from "../services/AuditLogService";
import { useStores } from "../hooks/useStores";
import type { WalmartStore } from "../utils/storeUtils";

const formatDate = (ts: number) => {
    const d = new Date(ts);
    const day = d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
    const time = d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
    return `${day}, ${time}`;
};

const parseStringArray = (raw: string) => {
    try {
        const parsed = JSON.parse(raw); return Array.isArray(parsed) ? parsed : [];
    }
    catch { return []; }
};

interface AuditLogModalProps {
    userId: string;
    onClose: () => void;
}

export default function AuditLogModal({ userId, onClose }: AuditLogModalProps) {
    const [loading, setLoading] = useState(true);
    const [logs, setLogs] = useState<AuditLog[]>([]);
    const [selected, setSelected] = useState<AuditLog | null>(null);
    const [closedDrawers, setClosedDrawers] = useState<Record<string, boolean>>({});
    const [mobileDetail, setMobileDetail] = useState(false);
    const { stores: walmartStores } = useStores<WalmartStore[]>("walmart");

    useEffect(() => {
        (async () => {
            try {
                const data = await getAdminUserAuditLogs(userId);
                const items = data || [];
                setLogs(items);
                if (items[0]?.changes) {
                    setSelected(items[0]);
                    setClosedDrawers({});
                } else {
                    setSelected(null);
                    setClosedDrawers({});
                }
            } catch {
                toast.error("Failed to load audit logs");
            } finally {
                setLoading(false);
            }
        })();
    }, [userId]);

    const selectLog = useCallback((log: AuditLog) => {
        if (!log.changes) return;
        setSelected(log);
        setClosedDrawers({});
        setMobileDetail(true);
    }, []);

    const storeNameById = useMemo(() => {
        const map: Record<string, string> = {};
        if (!Array.isArray(walmartStores)) return map;
        walmartStores.forEach((s) => {
            const id = String(s?.store_id || "");
            if (!id) return;
            const name = [s?.address, s?.city, s?.state].filter(Boolean).join(", ");
            map[id] = name || `Store #${id}`;
        });
        return map;
    }, [walmartStores]);


    const timeline = useMemo(() => (
        <>
            {loading && (
                <div className="p-6 flex flex-col items-center gap-2 text-sm text-gray-400 dark:text-dark-400">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Loading...</span>
                </div>
            )}
            {!loading && logs.length === 0 && (
                <div className="p-6 flex flex-col items-center text-center">
                    <div className="w-10 h-10 rounded-full bg-gray-100 dark:bg-dark-700 flex items-center justify-center mb-2">
                        <History className="w-5 h-5 text-gray-400 dark:text-dark-400" />
                    </div>
                    <p className="text-sm text-gray-500 dark:text-dark-400">No audit logs yet</p>
                </div>
            )}
            {!loading && logs.length > 0 && (
                <div className="p-2 space-y-0.5">
                    {logs.map((log) => {
                        if (!log.changes) return null;
                        const isActive = selected?.timestamp === log.timestamp;
                        const fields = Object.keys(log.changes);
                        return (
                            <button
                                key={`${log.userId}-${log.timestamp}`}
                                onClick={() => selectLog(log)}
                                className={`w-full text-left px-3 py-2.5 rounded-lg text-xs transition-all ${isActive
                                    ? "bg-gray-100/60 dark:bg-dark-700/50 ring-1 ring-gray-200 dark:ring-dark-600"
                                    : "hover:bg-gray-50 dark:hover:bg-dark-700/30"
                                    }`}
                            >
                                <p className={`font-medium ${isActive ? "text-gray-900 dark:text-white" : "text-gray-700 dark:text-dark-200"}`}>
                                    {formatDate(log.timestamp)}
                                </p>
                                <div className="flex flex-wrap gap-1 mt-1">
                                    {fields.map((f) => (
                                        <span key={f} className="px-1.5 py-0.5 rounded bg-gray-200 dark:bg-dark-600 text-[10px] text-gray-600 dark:text-dark-300 font-medium">
                                            {f}
                                        </span>
                                    ))}
                                </div>
                            </button>
                        );
                    })}
                </div>
            )}
        </>
    ), [loading, logs, selected?.timestamp, selectLog, formatDate]);

    const detail = useMemo(() => selected && (
        <div className="p-4">
            <div className="space-y-2">
                {Object.entries(selected.changes).map(([key, change]) => (
                    <div key={key} className="rounded-lg border border-gray-200 dark:border-dark-600 overflow-hidden">
                        <button
                            onClick={() => setClosedDrawers((prev) => ({ ...prev, [key]: !prev[key] }))}
                            className="w-full flex items-center justify-between px-3 py-2.5 text-left bg-gray-50 dark:bg-dark-700 hover:bg-gray-100 dark:hover:bg-dark-600 transition-colors"
                        >
                            <span className="text-xs font-semibold text-gray-800 dark:text-white">{key}</span>
                            {closedDrawers[key]
                                ? <ChevronRight className="w-3.5 h-3.5 text-gray-400 dark:text-dark-400" />
                                : <ChevronDown className="w-3.5 h-3.5 text-gray-400 dark:text-dark-400" />
                            }
                        </button>
                        {!closedDrawers[key] && (
                            <div className="px-3 py-3 space-y-3 bg-white dark:bg-dark-800">
                                <div>
                                    <p className="text-[10px] uppercase tracking-wide text-red-400 dark:text-red-500 font-semibold mb-1">Old</p>
                                    {key === "storeIds" && (
                                        <div className="bg-red-50 dark:bg-red-900/10 border border-red-100 dark:border-red-900/20 rounded-md p-2.5 space-y-1.5 max-h-50 overflow-auto">
                                            {parseStringArray(change.old).map((id) => (
                                                <div key={`old-${id}`} className="text-[11px] text-gray-700 dark:text-dark-200 break-all">
                                                    <span className="font-semibold">#{id}</span> - {storeNameById[id] || `Store #${id}`}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <p className="text-[10px] uppercase tracking-wide text-green-500 dark:text-green-400 font-semibold mb-1">New</p>
                                    {key === "storeIds" && (
                                        <div className="bg-green-50 dark:bg-green-900/10 border border-green-100 dark:border-green-900/20 rounded-md p-2.5 space-y-1.5 max-h-50 overflow-auto">
                                            {parseStringArray(change.new).map((id) => (
                                                <div key={`new-${id}`} className="text-[11px] text-gray-700 dark:text-dark-200 break-all">
                                                    <span className="font-semibold">#{id}</span> - {storeNameById[id] || `Store #${id}`}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    ), [selected, closedDrawers, storeNameById]);

    return createPortal(
        <div className="fixed inset-0 z-[9999] bg-black/30 dark:bg-black/40 backdrop-blur-sm flex items-center justify-center md:p-4" onClick={onClose}>
            <div className="bg-white dark:bg-dark-800 w-full h-full md:max-w-3xl md:max-h-[85vh] md:rounded-xl shadow-2xl flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="px-5 py-3.5 border-b border-gray-200 dark:border-dark-700 bg-gray-50 dark:bg-dark-700/50 flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                        {mobileDetail && (
                            <button onClick={() => setMobileDetail(false)} className="md:hidden p-1 -ml-1 hover:bg-gray-200 dark:hover:bg-dark-600 rounded-lg transition-colors">
                                <ArrowLeft className="w-4 h-4 text-gray-500 dark:text-dark-400" />
                            </button>
                        )}
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                            {mobileDetail && selected ? formatDate(selected.timestamp) : "Audit Log"}
                        </h3>
                        {!mobileDetail && !loading && <span className="text-xs text-gray-400 dark:text-dark-400">{logs.length} entries</span>}
                    </div>
                    <button onClick={onClose} className="p-1.5 hover:bg-gray-200 dark:hover:bg-dark-600 rounded-lg transition-colors">
                        <X className="w-4 h-4 text-gray-500 dark:text-dark-400" />
                    </button>
                </div>

                {/* Mobile: toggle between list and detail */}
                <div className="md:hidden flex-1 overflow-y-auto">
                    {mobileDetail ? detail : timeline}
                </div>

                {/* Desktop: side-by-side */}
                <div className="hidden md:grid md:grid-cols-[240px_1fr] min-h-0 flex-1">
                    <div className="border-r border-gray-200 dark:border-dark-700 overflow-y-auto">
                        {timeline}
                    </div>
                    <div className="overflow-y-auto">
                        {detail || (
                            <div className="flex flex-col items-center justify-center h-full p-6 text-center">
                                <History className="w-8 h-8 text-gray-300 dark:text-dark-500 mb-2" />
                                <p className="text-sm text-gray-400 dark:text-dark-400">Select an entry to view details</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
}
